import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft, Check, Moon, Sun, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { toast } from "sonner";
import T from "../components/T";
import { SUPPORTED_LANGUAGES, setLanguage as setI18nLanguage, getLanguage } from "../i18n";
import { isNative } from "../lib/native";

const STAGE_IDS = [
  "dating", "relationship", "married", "leaving",
  "already_left", "recovering", "co_parenting", "unsure",
];

const NEED_IDS = [
  "red_flags", "emotional_confusion", "nervous_system", "safety_planning",
  "documentation", "trusted_support", "financial_rebuilding", "dating_discernment",
];

// Optional onboarding context — collected ONCE, used to personalise the
// Sanctuary library (e.g. surface the LGBTQ+ relationships module first
// when relevant). Never required. Always editable from Settings.
const PARTNER_OPTIONS = [
  { id: "man",             key: "onboarding.step_partner.opt_man" },
  { id: "woman",           key: "onboarding.step_partner.opt_woman" },
  { id: "nonbinary",       key: "onboarding.step_partner.opt_nonbinary" },
  { id: "other",           key: "onboarding.step_partner.opt_other" },
  { id: "prefer_not_say",  key: "onboarding.step_partner.opt_prefer_not_say" },
];

const TOTAL_STEPS = 6; // language, stage, needs, partner, theme, name

export default function Onboarding() {
  const navigate = useNavigate();
  const { saveOnboarding } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  const [step, setStep] = useState(0);
  const [lang, setLang] = useState(getLanguage());
  const [stage, setStage] = useState(null);
  const [needs, setNeeds] = useState([]);
  // partner_gender stays null when the user skips the new step. Backend
  // treats null as "no preference set" and shows the general experience.
  const [partner, setPartner] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);

  // Keep i18n in sync when user picks a language tile.
  useEffect(() => { setI18nLanguage(lang); }, [lang]);

  const toggleNeed = (id) =>
    setNeeds((n) => (n.includes(id) ? n.filter((x) => x !== id) : [...n, id]));

  const next = async () => {
    if (step === 1 && !stage) return toast.info(t("onboarding.pick_closest"));
    if (step === TOTAL_STEPS - 1) {
      try {
        setBusy(true);
        await saveOnboarding({
          life_stage: stage,
          support_needs: needs,
          theme,
          display_name: displayName || null,
          language: lang,
          partner_gender: partner || null,
        });
        // Belt-and-suspenders: remember locally that onboarding is done,
        // so Gate / Welcome never bounce the user back if /me lags on a
        // cold reload (e.g. on Capacitor WKWebView).
        try { window.localStorage.setItem("ss_onboarded", "1"); } catch { /* ignore */ }

        // On native iOS (Capacitor) the React-Router history.replaceState
        // path navigation to `/today` is occasionally swallowed by the
        // WKWebView's app-bound-domain rules, leaving the user on an
        // inconsistent screen. A hard reload to /today is fully reliable:
        // the bundle's WKURLSchemeHandler falls back to index.html, React
        // mounts fresh, Gate sees onboarded=true, and renders Today.
        if (isNative()) {
          window.location.replace("/today");
        } else {
          navigate("/today", { replace: true });
        }
      } catch (e) {
        // --- TEMPORARY DIAGNOSTIC (remove once onboarding submit is green) ---
        // Surface the real failure on-screen because TestFlight installs have
        // no dev-tools console.
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e?.response?.data || e?.message || "unknown";
        const url = e?.config?.baseURL ? `${e.config.baseURL}${e.config.url}` : "(no url)";
        const summary = `[onboarding] ${status || "no-status"} · ${url} · ${
          typeof detail === "string" ? detail : JSON.stringify(detail)
        }`;
        // Toast (visible, long-lived) + alert (impossible-to-miss) + console.
        // eslint-disable-next-line no-console
        console.error(summary, e);
        toast.error(summary, { duration: 12000 });
        try { window.alert(summary); } catch { /* ignore */ }
      } finally {
        setBusy(false);
      }
      return;
    }
    setStep((s) => s + 1);
  };

  return (
    <div className={`${theme === "night" ? "ss-night-bg" : "ss-soft-bg"} min-h-[100dvh] w-full`}>
      <div className="relative mx-auto w-full max-w-md min-h-[100dvh] px-8 py-8 flex flex-col">
        {/* progress dots */}
        <div className="flex gap-2 items-center">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all ${
                i <= step ? "bg-brand" : "bg-foreground/10"
              }`}
            />
          ))}
        </div>

        <div className="mt-12 flex-1">
          {step === 0 && (
            <div data-testid="onboarding-step-language" className="animate-fade-up">
              <p className="font-script text-2xl text-brand">{t("onboarding.step_language.kicker")}</p>
              <h2 className="font-serif text-3xl mt-1 leading-tight">
                <T i18nKey="onboarding.step_language.h" />
              </h2>
              <p className="mt-2 text-sm text-foreground/60">{t("onboarding.step_language.sub")}</p>
              <div className="mt-6 space-y-2.5">
                {SUPPORTED_LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`w-full text-left px-5 py-4 rounded-2xl border flex items-center gap-3 transition-all ${
                      lang === l.code
                        ? "bg-brand text-white border-brand shadow-md"
                        : "bg-card border-border hover:border-brand/50"
                    }`}
                  >
                    <Globe className="w-5 h-5 opacity-80" />
                    <span className="font-medium">{l.native}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboarding-step-stage" className="animate-fade-up">
              <p className="font-script text-2xl text-brand">{t("onboarding.step_stage.kicker")}</p>
              <h2 className="font-serif text-3xl mt-1 leading-tight">
                <T i18nKey="onboarding.step_stage.h" />
              </h2>
              <p className="mt-2 text-sm text-foreground/60">{t("onboarding.step_stage.sub")}</p>
              <div className="mt-6 space-y-2.5">
                {STAGE_IDS.map((id) => (
                  <button
                    key={id}
                    data-testid={`stage-${id}`}
                    onClick={() => setStage(id)}
                    className={`w-full text-left px-5 py-4 rounded-2xl border transition-all ${
                      stage === id
                        ? "bg-brand text-white border-brand shadow-md"
                        : "bg-card border-border hover:border-brand/50"
                    }`}
                  >
                    <span className="font-medium">{t(`onboarding.stages.${id}`)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-needs" className="animate-fade-up">
              <p className="font-script text-2xl text-brand">{t("onboarding.step_needs.kicker")}</p>
              <h2 className="font-serif text-3xl mt-1 leading-tight">
                <T i18nKey="onboarding.step_needs.h" />
              </h2>
              <p className="mt-2 text-sm text-foreground/60">{t("onboarding.step_needs.sub")}</p>
              <div className="mt-6 grid grid-cols-1 gap-2.5">
                {NEED_IDS.map((id) => {
                  const active = needs.includes(id);
                  return (
                    <button
                      key={id}
                      data-testid={`need-${id}`}
                      onClick={() => toggleNeed(id)}
                      className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl border transition-all text-left ${
                        active
                          ? "bg-brand/10 border-brand text-foreground"
                          : "bg-card border-border hover:border-brand/40"
                      }`}
                    >
                      <span className="font-medium">{t(`onboarding.needs.${id}`)}</span>
                      <span
                        className={`w-5 h-5 rounded-full grid place-items-center border ${
                          active ? "bg-brand border-brand text-white" : "border-foreground/30"
                        }`}
                      >
                        {active && <Check className="w-3 h-3" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-partner" className="animate-fade-up">
              <p className="font-script text-2xl text-brand">{t("onboarding.step_partner.kicker")}</p>
              <h2 className="font-serif text-3xl mt-1 leading-tight">
                <T i18nKey="onboarding.step_partner.h" />
              </h2>
              <p className="mt-2 text-sm text-foreground/60">{t("onboarding.step_partner.sub")}</p>
              <div className="mt-5 space-y-2">
                {PARTNER_OPTIONS.map((opt) => {
                  const active = partner === opt.id;
                  return (
                    <button
                      key={opt.id}
                      data-testid={`partner-${opt.id}`}
                      onClick={() => setPartner(opt.id)}
                      className={`w-full text-left rounded-2xl border p-4 transition-all ${
                        active ? "border-brand bg-brand/5" : "border-border hover:border-brand/40"
                      }`}
                    >
                      <span className="text-base text-foreground">{t(opt.key)}</span>
                    </button>
                  );
                })}
              </div>
              <button
                data-testid="partner-skip"
                onClick={() => { setPartner(null); next(); }}
                className="mt-4 text-sm text-foreground/55 hover:text-brand"
              >
                {t("onboarding.step_partner.skip")}
              </button>
            </div>
          )}

          {step === 4 && (
            <div data-testid="onboarding-step-theme" className="animate-fade-up">
              <p className="font-script text-2xl text-brand">{t("onboarding.step_theme.kicker")}</p>
              <h2 className="font-serif text-3xl mt-1 leading-tight">
                <T i18nKey="onboarding.step_theme.h" />
              </h2>
              <p className="mt-2 text-sm text-foreground/60">{t("onboarding.step_theme.sub")}</p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  data-testid="theme-soft"
                  onClick={() => setTheme("soft")}
                  className={`rounded-3xl p-5 border text-left transition-all ${
                    theme === "soft" ? "border-brand shadow-md" : "border-border"
                  }`}
                  style={{ background: "linear-gradient(160deg,#FFF5F7,#FDFBF7)" }}
                >
                  <Sun className="w-5 h-5 text-[#EC4899]" />
                  <div className="mt-3 font-serif text-2xl italic text-[#EC4899]">{t("onboarding.step_theme.soft")}</div>
                  <p className="text-xs text-[#5b4750] mt-1">{t("onboarding.step_theme.soft_desc")}</p>
                </button>
                <button
                  data-testid="theme-night"
                  onClick={() => setTheme("night")}
                  className={`rounded-3xl p-5 border text-left transition-all ${
                    theme === "night" ? "border-brand shadow-md" : "border-border"
                  }`}
                  style={{ background: "linear-gradient(160deg,#1a1a24,#0f0f13)" }}
                >
                  <Moon className="w-5 h-5 text-[#D4AF37]" />
                  <div className="mt-3 font-serif text-2xl italic text-[#D4AF37]">{t("onboarding.step_theme.night")}</div>
                  <p className="text-xs text-[#A09B9E] mt-1">{t("onboarding.step_theme.night_desc")}</p>
                </button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="onboarding-step-name" className="animate-fade-up">
              <p className="font-script text-2xl text-brand">{t("onboarding.step_name.kicker")}</p>
              <h2 className="font-serif text-3xl mt-1 leading-tight">
                <T i18nKey="onboarding.step_name.h" />
              </h2>
              <p className="mt-2 text-sm text-foreground/60">{t("onboarding.step_name.sub")}</p>
              <Input
                data-testid="onboarding-name-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t("onboarding.step_name.placeholder")}
                className="mt-5 h-14 rounded-2xl bg-card border-border px-5 text-base"
              />
              <p className="mt-6 text-sm text-foreground/60 leading-relaxed">
                {t("onboarding.step_name.note")}
              </p>
            </div>
          )}
        </div>

        <div className="pt-6 flex items-center gap-3">
          {step > 0 && (
            <Button
              data-testid="onboarding-back-btn"
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              className="h-13 rounded-full px-5"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> {t("common.back")}
            </Button>
          )}
          <Button
            data-testid="onboarding-next-btn"
            onClick={next}
            disabled={busy}
            className="flex-1 h-14 rounded-full bg-brand hover:bg-brand/90 text-white text-base"
          >
            {step < TOTAL_STEPS - 1
              ? t("common.continue")
              : busy
              ? t("common.saving")
              : t("onboarding.enter_softly")}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
